import type { RevenuePaymentLogsListParams, RevenuePaymentLogsSortKey, RevenuePaymentLogsRevenuePaymentLogsFiltersProps } from '../types/pages'
/**
 * Revenue Payment Logs — React parity with Angular `ReportsPaymentLogsComponent`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronsUpDown, DollarSign, Download, Eye, Loader2, Receipt, Wallet } from 'lucide-react'
import { PageShell } from '@/components/layout/PageShell'
import { ListColumnsMenu } from '@/components/ui/ListColumnsMenu'
import { ListSearchToolbar } from '@/components/ui/ListSearchToolbar'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { ListFilterDrawer } from '@/components/ui/ListFilterDrawer'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { TableSearchFilterEmptyTableRow } from '@/components/ui/TableSearchFilterEmptyState'
import {
  SearchableSelect,
  type SearchableSelectOption,
} from '@/components/ui/SearchableSelect'
import { useAppSelector } from '@/app/hooks'
import { selectIsAuthenticated, selectUser } from '@/features/auth/authSlice'
import { formatDateForAPI } from '@/features/invitee/utils/inviteeFormatters'
import {
  getCompanies,
  getProperties,
} from '@/features/invitee/services/inviteeService'
import { getBrowserApiBaseUrl } from '@/config/env'
import { getAuthTokenFromStorage } from '@/lib/utils/storage'
import { showApiError } from '@/lib/utils/notifications'
import { formatAmount } from '@/lib/utils/formatters'
import { useConfigurableListColumns } from '@/hooks/useConfigurableListColumns'
import { useUserRole } from '@/hooks/useUserRole'
import { cn } from '@lib/utils/helpers'
import { ReportOverviewMetrics } from '../components/ReportOverviewMetrics'
import { ReportTableScroll } from '../components/ReportTableScroll'
import reportStyles from '../styles/reportsTable.module.css'
import { ReportDateRangePicker } from '../components/ReportDateRangePicker'
import { getGatewayStatuses, getRevenuePaymentLogList } from '../services/reportsService'
import type { GatewayStatusMetaMap, RevenuePaymentLogListResponse, RevenuePaymentLogRow } from '../types/api'
import { ymdToDmy } from '../utils/reportDateParams'
import {
  buildPaymentLogsSessionFromParams,
  clearPaymentLogsSession,
  readPaymentLogsSession,
  sessionFilterToTotalContractsPatch,
  writePaymentLogsSession,
} from '../utils/paymentLogsSessionFilter'
import {
  renderRevenuePaymentLogCell,
  revenuePaymentLogCellClassName,
} from '../utils/revenuePaymentLogCells'
import {
  createEmptyRevenuePaymentLogsParams,
  normalizeRevenuePaymentLogsFilters,
  PAYMENT_STATUS_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
} from '../constants/pages/revenuePaymentLogsPage'
import {
  REVENUE_PAYMENT_LOGS_COLUMN_DEFS,
  REVENUE_PAYMENT_LOGS_COLUMN_OPTIONS,
  REVENUE_PAYMENT_LOGS_COLUMN_VISIBILITY_STORAGE_KEY,
  REVENUE_PAYMENT_LOGS_LOCKED_COLUMN_IDS,
} from '../constants/listColumns'
import {
  LABEL_CLASS,
  REPORT_EXPORT_BUTTON_CLASS,
} from '../constants/configuredReportStyles'
import {
  PAYMENT_SOURCE_FILTER_OPTIONS,
  SPECIAL_NOTE_FILTER_OPTIONS,
} from '../constants/filterOptions'

function initialParams(): RevenuePaymentLogsListParams {
  const session = readPaymentLogsSession()
  if (!session) return createEmptyRevenuePaymentLogsParams()
  const { patch } = sessionFilterToTotalContractsPatch(session)
  return createEmptyRevenuePaymentLogsParams({
    ...patch,
    type: patch.type ?? [],
    status: patch.status ?? [],
    gateway_status: patch.gateway_status ?? [],
  })
}

function appendArrayParams(qs: URLSearchParams, key: string, values: string[]) {
  values.forEach((value) => {
    if (value) qs.append(`${key}[]`, value)
  })
}

/**
 * List query params — Angular `getRequestBody()` for GET `/api/payment_log`.
 * Dates as DD-MM-YYYY; multi-values as `type[]` / `status[]` / `gateway_status[]`.
 * Empty filters are omitted (do not inject default type[]).
 */
function buildPaymentLogQueryParams(p: RevenuePaymentLogsListParams): URLSearchParams {
  const qs = new URLSearchParams()
  qs.set('page', String(p.page))
  qs.set('perPage', String(p.perPage))
  qs.set('sortBy', p.sortBy)
  qs.set('orderBy', p.orderBy)
  if (p.q.trim()) qs.set('q', p.q.trim())
  if (p.company_id) qs.set('company_id', p.company_id)
  if (p.property_id) qs.set('property_id', p.property_id)
  const fromApi = ymdToDmy(p.fromDt)
  const toApi = ymdToDmy(p.toDt)
  if (fromApi) qs.set('fromDt', fromApi)
  if (toApi) qs.set('toDt', toApi)
  const csFrom = ymdToDmy(p.contract_start_fromDt)
  const csTo = ymdToDmy(p.contract_start_toDt)
  if (csFrom) qs.set('contract_start_fromDt', csFrom)
  if (csTo) qs.set('contract_start_toDt', csTo)
  const fundingFrom = ymdToDmy(p.funding_fromDt)
  const fundingTo = ymdToDmy(p.funding_toDt)
  if (fundingFrom) qs.set('funding_fromDt', fundingFrom)
  if (fundingTo) qs.set('funding_toDt', fundingTo)
  appendArrayParams(qs, 'type', p.type)
  if (p.source.trim()) qs.set('source', p.source.trim())
  appendArrayParams(qs, 'status', p.status)
  appendArrayParams(qs, 'gateway_status', p.gateway_status)
  if (p.special_note.trim()) qs.set('special_note', p.special_note.trim())
  return qs
}

/** Export URL — Angular `export()` uses `type[i]` / `status[i]` / `gateway_status[i]`. */
function buildPaymentLogExportHref(p: RevenuePaymentLogsListParams): string {
  const token = getAuthTokenFromStorage()
  if (!token) return ''
  const qs = new URLSearchParams()
  qs.set('page', String(p.page))
  qs.set('perPage', String(p.perPage))
  qs.set('sortBy', p.sortBy)
  qs.set('orderBy', p.orderBy)
  if (p.q.trim()) qs.set('q', p.q.trim())
  if (p.company_id) qs.set('company_id', p.company_id)
  if (p.property_id) qs.set('property_id', p.property_id)
  const fromApi = ymdToDmy(p.fromDt)
  const toApi = ymdToDmy(p.toDt)
  if (fromApi) qs.set('fromDt', fromApi)
  if (toApi) qs.set('toDt', toApi)
  const csFrom = ymdToDmy(p.contract_start_fromDt)
  const csTo = ymdToDmy(p.contract_start_toDt)
  if (csFrom) qs.set('contract_start_fromDt', csFrom)
  if (csTo) qs.set('contract_start_toDt', csTo)
  const fundingFrom = ymdToDmy(p.funding_fromDt)
  const fundingTo = ymdToDmy(p.funding_toDt)
  if (fundingFrom) qs.set('funding_fromDt', fundingFrom)
  if (fundingTo) qs.set('funding_toDt', fundingTo)
  p.type.forEach((value, index) => {
    if (value) qs.set(`type[${index}]`, value)
  })
  p.status.forEach((value, index) => {
    if (value) qs.set(`status[${index}]`, value)
  })
  p.gateway_status.forEach((value, index) => {
    if (value) qs.set(`gateway_status[${index}]`, value)
  })
  if (p.source.trim()) qs.set('source', p.source.trim())
  if (p.special_note.trim()) qs.set('special_note', p.special_note.trim())
  qs.set('access_token', token)
  qs.set('view', 'xlsx')
  return `${getBrowserApiBaseUrl()}/api/export/payment_log?${qs.toString()}`
}

function countActiveFilters(
  p: RevenuePaymentLogsListParams,
  lockedCompanyId?: string,
  lockedPropertyId?: string,
): number {
  let count = 0
  if (p.q.trim()) count++
  if (p.company_id && p.company_id !== lockedCompanyId) count++
  if (p.property_id && p.property_id !== lockedPropertyId) count++
  if (p.special_note) count++
  if (p.type.length) count++
  if (p.source) count++
  if (p.status.length) count++
  if (p.gateway_status.length) count++
  if (p.fromDt || p.toDt) count++
  if (p.contract_start_fromDt || p.contract_start_toDt) count++
  if (p.funding_fromDt || p.funding_toDt) count++
  return count
}

function gatewayOptionsFromMeta(
  meta: GatewayStatusMetaMap,
): SearchableSelectOption[] {
  const seen = new Set<string>()
  const options: SearchableSelectOption[] = []
  Object.values(meta).forEach((bucket) => {
    Object.entries(bucket ?? {}).forEach(([code, status]) => {
      if (seen.has(code)) return
      seen.add(code)
      options.push({
        value: code,
        label: status.gateway_status_display || code,
      })
    })
  })
  return options
}


function RevenuePaymentLogsFilters({
  filters,
  isOpen,
  onClose,
  onApply,
  onReset,
  gatewayStatusOptions,
}: RevenuePaymentLogsRevenuePaymentLogsFiltersProps) {
  const user = useAppSelector(selectUser)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const { companyId, propertyId } = useUserRole()
  const lockedCompanyId = companyId != null ? String(companyId) : undefined
  const lockedPropertyId = propertyId != null ? String(propertyId) : undefined

  const [draft, setDraft] = useState<RevenuePaymentLogsListParams>(filters)
  const [companies, setCompanies] = useState<SearchableSelectOption[]>([])
  const [properties, setProperties] = useState<SearchableSelectOption[]>([])
  const [loadingCompanies, setLoadingCompanies] = useState(false)
  const [loadingProperties, setLoadingProperties] = useState(false)

  useEffect(() => {
    if (isOpen) setDraft(filters)
  }, [isOpen, filters])

  const loadCompanies = async (search: string) => {
    if (!isAuthenticated || lockedCompanyId) return
    setLoadingCompanies(true)
    try {
      const data = await getCompanies(search)
      setCompanies(
        data.map((c) => ({
          value: String(c.company_id),
          label: c.company_name || String(c.company_id),
        })),
      )
    } finally {
      setLoadingCompanies(false)
    }
  }

  const loadProperties = async (search: string) => {
    if (!isAuthenticated || lockedPropertyId) return
    setLoadingProperties(true)
    try {
      const data = await getProperties(
        search,
        draft.company_id || lockedCompanyId,
      )
      setProperties(
        data.map((p) => ({
          value: String(p.property_id),
          label: p.property_name || String(p.property_id),
        })),
      )
    } finally {
      setLoadingProperties(false)
    }
  }

  useEffect(() => {
    if (!isAuthenticated || !isOpen) return
    if (!lockedCompanyId) void loadCompanies('')
    if (!lockedPropertyId) void loadProperties('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    isOpen,
    lockedCompanyId,
    lockedPropertyId,
    draft.company_id,
  ])

  /** Apply on change (company/property/invitee convention) — Done only closes the drawer. */
  const patch = (partial: Partial<RevenuePaymentLogsListParams>) => {
    const merged = { ...draft, ...partial }
    setDraft(merged)
    onApply(
      normalizeRevenuePaymentLogsFilters({
        ...merged,
        company_id: lockedCompanyId || merged.company_id,
        property_id: lockedPropertyId || merged.property_id,
      }),
    )
  }

  const emptyDraft = (): RevenuePaymentLogsListParams =>
    createEmptyRevenuePaymentLogsParams({
      company_id: lockedCompanyId || '',
      property_id: lockedPropertyId || '',
      // Keep list pagination defaults on drawer reset (Angular keeps perPage/sort).
      perPage: filters.perPage,
      sortBy: filters.sortBy,
      orderBy: filters.orderBy,
    })

  const companyOptions = lockedCompanyId
    ? [
        {
          value: lockedCompanyId,
          label: user?.current_role?.company_name || lockedCompanyId,
        },
      ]
    : companies
  const propertyOptions = lockedPropertyId
    ? [
        {
          value: lockedPropertyId,
          label: user?.current_role?.property_name || lockedPropertyId,
        },
      ]
    : properties

  return (
    <ListFilterDrawer
      isOpen={isOpen}
      onClose={onClose}
      onReset={() => {
        setDraft(emptyDraft())
        onReset()
      }}
      doneLabel="Close"
      testIdPrefix="revenue-payment-logs"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <label className={LABEL_CLASS}>Company</label>
          <SearchableSelect
            options={companyOptions}
            value={lockedCompanyId || draft.company_id || ''}
            onChange={(val) => {
              const company_id = Array.isArray(val) ? (val[0] ?? '') : val
              patch({ company_id, property_id: '' })
            }}
            onSearch={lockedCompanyId ? undefined : loadCompanies}
            onOpen={lockedCompanyId ? undefined : () => loadCompanies('')}
            loading={loadingCompanies}
            disabled={Boolean(lockedCompanyId)}
            placeholder="All Companies"
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Property</label>
          <SearchableSelect
            options={propertyOptions}
            value={lockedPropertyId || draft.property_id || ''}
            onChange={(val) => {
              const property_id = Array.isArray(val) ? (val[0] ?? '') : val
              patch({ property_id })
            }}
            onSearch={lockedPropertyId ? undefined : loadProperties}
            onOpen={lockedPropertyId ? undefined : () => loadProperties('')}
            loading={loadingProperties}
            disabled={Boolean(lockedPropertyId)}
            placeholder="All Properties"
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Special Note</label>
          <SearchableSelect
            searchable={false}
            options={SPECIAL_NOTE_FILTER_OPTIONS}
            value={draft.special_note}
            onChange={(val) => {
              const v = Array.isArray(val) ? val[0] : val
              patch({ special_note: v || '' })
            }}
            placeholder="Special Note"
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Payment Type</label>
          <SearchableSelect
            multiple
            options={PAYMENT_TYPE_OPTIONS}
            value={draft.type}
            onChange={(val) => patch({ type: Array.isArray(val) ? val : [] })}
            placeholder="All Payment Types"
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Source</label>
          <SearchableSelect
            searchable={false}
            options={PAYMENT_SOURCE_FILTER_OPTIONS}
            value={draft.source}
            onChange={(val) => {
              const v = Array.isArray(val) ? val[0] : val
              patch({ source: v || '' })
            }}
            placeholder="All Source"
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Statuses</label>
          <SearchableSelect
            multiple
            options={PAYMENT_STATUS_OPTIONS}
            value={draft.status}
            onChange={(val) => patch({ status: Array.isArray(val) ? val : [] })}
            placeholder="All Statuses"
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Gateway Status</label>
          <SearchableSelect
            multiple
            options={gatewayStatusOptions}
            value={draft.gateway_status}
            onChange={(val) =>
              patch({ gateway_status: Array.isArray(val) ? val : [] })
            }
            placeholder="Gateway Status"
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Transaction Dates</label>
          <ReportDateRangePicker
            startDate={draft.fromDt}
            endDate={draft.toDt}
            onChange={(start, end) =>
              patch({
                fromDt: start ? formatDateForAPI(start) : '',
                toDt: end ? formatDateForAPI(end) : '',
              })
            }
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Contract Start Dates</label>
          <ReportDateRangePicker
            startDate={draft.contract_start_fromDt}
            endDate={draft.contract_start_toDt}
            onChange={(start, end) =>
              patch({
                contract_start_fromDt: start ? formatDateForAPI(start) : '',
                contract_start_toDt: end ? formatDateForAPI(end) : '',
              })
            }
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Funding Dates</label>
          <ReportDateRangePicker
            startDate={draft.funding_fromDt}
            endDate={draft.funding_toDt}
            onChange={(start, end) =>
              patch({
                funding_fromDt: start ? formatDateForAPI(start) : '',
                funding_toDt: end ? formatDateForAPI(end) : '',
              })
            }
          />
        </div>
      </div>
    </ListFilterDrawer>
  )
}

export function RevenuePaymentLogsPage() {
  const { hasWriteAccess, companyId, propertyId } = useUserRole()
  const canOpenApplication = hasWriteAccess('application')
  const lockedCompanyId = companyId != null ? String(companyId) : undefined
  const lockedPropertyId = propertyId != null ? String(propertyId) : undefined

  const [params, setParams] = useState<RevenuePaymentLogsListParams>(initialParams)
  const [searchDraft, setSearchDraft] = useState(() => params.q)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [rows, setRows] = useState<RevenuePaymentLogRow[]>([])
  const [totals, setTotals] =
    useState<RevenuePaymentLogListResponse['total_count']>()
  const [pageOptions, setPageOptions] = useState({
    current: 1,
    last: 1,
    total: 0,
  })
  const [loading, setLoading] = useState(false)
  const [gatewayMeta, setGatewayMeta] = useState<GatewayStatusMetaMap>({})
  const [gatewayStatusOptions, setGatewayStatusOptions] = useState<
    SearchableSelectOption[]
  >([])
  const [exportWarnOpen, setExportWarnOpen] = useState(false)

  const columnsController = useConfigurableListColumns({
    storageKey: REVENUE_PAYMENT_LOGS_COLUMN_VISIBILITY_STORAGE_KEY,
    columnOptions: REVENUE_PAYMENT_LOGS_COLUMN_OPTIONS,
    lockedColumnIds: [...REVENUE_PAYMENT_LOGS_LOCKED_COLUMN_IDS],
  })
  const visibleColumns = useMemo(() => {
    const byId = new Map(
      REVENUE_PAYMENT_LOGS_COLUMN_DEFS.map((column) => [column.id, column]),
    )
    return columnsController.tableColumnIds
      .map((columnId) => byId.get(columnId))
      .filter(
        (
          column,
        ): column is (typeof REVENUE_PAYMENT_LOGS_COLUMN_DEFS)[number] =>
          Boolean(column),
      )
  }, [columnsController.tableColumnIds])

  useEffect(() => {
    setSearchDraft(params.q)
  }, [params.q])

  useEffect(() => {
    void getGatewayStatuses()
      .then((meta) => {
        setGatewayMeta(meta)
        setGatewayStatusOptions(gatewayOptionsFromMeta(meta))
      })
      .catch(() => {
        /* optional metadata */
      })
  }, [])

  const load = useCallback(
    async (next: RevenuePaymentLogsListParams) => {
      setLoading(true)
      try {
        const scoped = {
          ...next,
          company_id: lockedCompanyId || next.company_id,
          property_id: lockedPropertyId || next.property_id,
        }
        const res = await getRevenuePaymentLogList(
          buildPaymentLogQueryParams(scoped),
        )
        setRows(res.data ?? [])
        setTotals(res.total_count)
        setPageOptions({
          current: res.current ?? 1,
          last: Math.max(1, res.last ?? 1),
          total: res.total ?? 0,
        })
        writePaymentLogsSession(buildPaymentLogsSessionFromParams(scoped))
      } catch (err) {
        showApiError(err)
      } finally {
        setLoading(false)
      }
    },
    [lockedCompanyId, lockedPropertyId],
  )

  useEffect(() => {
    void load(params)
  }, [load, params])

  useEffect(() => {
    const timer = setTimeout(() => {
      const nextQ = searchDraft.trim()
      if (nextQ !== params.q.trim()) {
        setParams((prev) => ({ ...prev, q: nextQ, page: 1 }))
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [searchDraft, params.q])

  const activeFilterCount = useMemo(
    () => countActiveFilters(params, lockedCompanyId, lockedPropertyId),
    [params, lockedCompanyId, lockedPropertyId],
  )

  const applyDrawerFilters = useCallback((next: RevenuePaymentLogsListParams) => {
    setParams(normalizeRevenuePaymentLogsFilters(next))
  }, [])

  const resetFilters = () => {
    setSearchDraft('')
    clearPaymentLogsSession()
    setParams(
      createEmptyRevenuePaymentLogsParams({
        company_id: lockedCompanyId || '',
        property_id: lockedPropertyId || '',
      }),
    )
  }

  const sort = (orderBy: RevenuePaymentLogsSortKey) => {
    setParams((prev) => ({
      ...prev,
      sortBy:
        prev.orderBy === orderBy && prev.sortBy === 'desc' ? 'asc' : 'desc',
      orderBy,
      page: 1,
    }))
  }

  const exportHref = useMemo(
    () =>
      buildPaymentLogExportHref({
        ...params,
        company_id: lockedCompanyId || params.company_id,
        property_id: lockedPropertyId || params.property_id,
      }),
    [params, lockedCompanyId, lockedPropertyId],
  )

  const showTotalsStrip = Boolean(totals && Object.keys(totals).length > 0)

  return (
    <div data-testid="revenue-payment-logs-page">
      <PageShell
        title="Revenue Payment Logs"
        pageHeaderTestIdRoot="revenue-payment-logs-page-header"
        contentClassName="!space-y-6 w-full max-w-none min-w-0"
      >
        <ListSearchToolbar
          searchValue={searchDraft}
          searchPlaceholder="Search.."
          onSearchChange={setSearchDraft}
          onOpenFilters={() => setIsFilterOpen(true)}
          activeFilterCount={activeFilterCount}
          testIdPrefix="revenue-payment-logs"
          actions={
            <>
              {exportHref ? (
                <a
                  href={exportHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    if (pageOptions.total > 10000) setExportWarnOpen(true)
                  }}
                  className={REPORT_EXPORT_BUTTON_CLASS}
                >
                  <Download className="h-[18px] w-[18px]" aria-hidden />
                  Export
                </a>
              ) : null}
              <ListColumnsMenu
                columns={columnsController}
                testIdPrefix="revenue-payment-logs"
              />
            </>
          }
        />

        <RevenuePaymentLogsFilters
          filters={params}
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={applyDrawerFilters}
          onReset={resetFilters}
          gatewayStatusOptions={gatewayStatusOptions}
        />

        {showTotalsStrip ? (
          <ReportOverviewMetrics
            title="Payment overview"
            subtitle="Current report totals"
            testIdPrefix="revenue-payment-logs"
            loading={loading}
            items={[
              {
                id: 'base-amount',
                label: 'Base Amount',
                value: formatAmount(totals?.base_amount ?? 0),
                icon: <DollarSign className="h-6 w-6" aria-hidden />,
                tone: 'blue',
              },
              {
                id: 'service-fee',
                label: 'Service Fee',
                value: formatAmount(totals?.service_fee ?? 0),
                icon: <Receipt className="h-6 w-6" aria-hidden />,
                tone: 'violet',
              },
              {
                id: 'total-amount',
                label: 'Total Amount',
                value: formatAmount(totals?.payment_amount ?? 0),
                icon: <Wallet className="h-6 w-6" aria-hidden />,
                tone: 'sky',
              },
            ]}
          />
        ) : null}

        <div className={reportStyles.tableCard}>
          <ListPaginationBar
            placement="header"
            currentPage={pageOptions.current}
            lastPage={pageOptions.last}
            totalRecords={pageOptions.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            testIdPrefix="revenue-payment-logs"
            summaryLayout="angular"
          />

          {loading ? (
            <div className="flex min-h-[12rem] items-center justify-center py-16">
              <Loader2
                className="h-8 w-8 animate-spin text-[rgb(21_151_255)]"
                aria-hidden
              />
            </div>
          ) : (
            <ReportTableScroll
              enabled={rows.length > 0}
              testIdPrefix="revenue-payment-logs"
            >
              <table
                className={cn(
                  reportStyles.listingTable,
                  'border-collapse text-left text-slate-800',
                  '[&_thead_th]:px-2 [&_tbody_td]:px-2 sm:[&_thead_th]:px-2.5 sm:[&_tbody_td]:px-2.5',
                )}
              >
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/90">
                    {visibleColumns.map((col) => {
                      const active = params.orderBy === col.sortKey
                      const sortKey = col.sortKey as RevenuePaymentLogsSortKey
                      return (
                        <th key={col.id} className="text-left">
                          <button
                            type="button"
                            onClick={() => sort(sortKey)}
                            className={cn(
                              'inline-flex items-center gap-1.5 whitespace-nowrap text-left font-semibold text-slate-600 hover:text-slate-900',
                              active && 'text-[rgb(21_151_255)]',
                            )}
                          >
                             <span>{col.label}</span>
                             <span className={cn('inline-flex items-center ml-1 text-[11px]', active ? 'text-[#1597ff]' : 'text-slate-400')}>
                               {active ? (params.sortBy === 'asc' ? '▲' : '▼') : <ChevronsUpDown className="h-3 w-3 shrink-0" aria-hidden />}
                             </span>
                           </button>
                        </th>
                      )
                    })}
                    <th
                      data-col="action"
                      className="text-right font-semibold text-slate-600"
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <TableSearchFilterEmptyTableRow
                      colSpan={visibleColumns.length + 1}
                      rowDataTestId="revenue-payment-logs-table-empty-row"
                      data-testid="revenue-payment-logs-table-empty-message"
                    />
                  ) : (
                    rows.map((row, index) => (
                        <tr
                          key={row.payment_log_id ?? `row-${index}`}
                          className="border-b border-slate-100"
                        >
                          {visibleColumns.map((col) => (
                            <td
                              key={col.id}
                              className={revenuePaymentLogCellClassName(col.id)}
                            >
                              {renderRevenuePaymentLogCell(col.id, row, {
                                canOpenApplication,
                                gatewayMeta,
                              })}
                            </td>
                          ))}
                          <td data-col="action" className="text-right">
                            {row.payment_log_id != null ? (
                              <Link
                                to={`/reports/payment-logs/${row.payment_log_id}`}
                                className="inline-flex rounded-lg p-2 text-slate-600 hover:bg-slate-100"
                                title="View Log"
                              >
                                <Eye className="h-4 w-4" aria-hidden />
                              </Link>
                            ) : null}
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </ReportTableScroll>
          )}

          <ListPaginationBar
            placement="footer"
            currentPage={pageOptions.current}
            lastPage={pageOptions.last}
            totalRecords={pageOptions.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            testIdPrefix="revenue-payment-logs"
            summaryLayout="angular"
          />
        </div>
      </PageShell>

      <ConfirmDialog
        isOpen={exportWarnOpen}
        title="Export Warning"
        message="The results contain more than 10,000 records. If you export, some Filters or Pivot Table features may not work correctly. Consider narrowing filters for fewer than 10,000 rows."
        primaryLabel="Ok"
        onPrimary={() => setExportWarnOpen(false)}
        secondaryLabel=""
        onSecondary={() => setExportWarnOpen(false)}
        onDismiss={() => setExportWarnOpen(false)}
        sectionedChrome
        messageAlign="center"
        messageWeight="normal"
        presentation="saveChanges"
      />
    </div>
  )
}
